import { createContext, useContext } from "react";

import type { DivergenciaSimulacaoApi, SimulacaoAtivaApi } from "../simulacao.gateway";
import type { AlteracaoSimulada, CenarioSimulacao, SimulacaoState } from "./simulacao.model";

export interface SimulacaoContextValue {
  state: SimulacaoState;
  /** Cenário em andamento; `null` fora da simulação. */
  cenario: CenarioSimulacao | null;
  /** Contador da faixa. Vem de `qtdAlteracoes` da API quando há simulação persistida. */
  alteracoes: number;
  /** ID da simulação ativa no servidor; null quando não há simulação persistida. */
  simulacaoId: string | null;
  /** Versão para o cabeçalho If-Match (descarte e aplicação). */
  versao: number | null;
  /** Divergências detectadas pela API ao carregar a simulação ativa. */
  divergenciasApi: readonly DivergenciaSimulacaoApi[];
  /** `true` enquanto o GET /ativa inicial está em andamento. */
  loading: boolean;
  /**
   * `true` quando há uma simulação ativa no servidor, mesmo que o banner
   * esteja oculto (após "Sair" sem descartar).
   */
  haSimulacaoAtiva: boolean;
  /**
   * Mapa da chave local (`chaveAlteracao`) para o ID da instrução na API.
   * Populado após GET /ativa ou uma instrução gravada com `simular: true`.
   */
  mapaChaveParaItemId: ReadonlyMap<string, string>;
  entrar: () => void;
  sair: () => void;
  incluir: (alteracao: AlteracaoSimulada) => void;
  remover: (chave: string) => void;
  /** Troca o cenário inteiro (ex.: só as alterações não aplicadas, com ids já reais). */
  redefinir: (alteracoes: readonly AlteracaoSimulada[]) => void;
  /** Atualiza o estado persistido após um POST /simulacoes ou GET /ativa. */
  hidratarSimulacaoAtiva: (dados: SimulacaoAtivaApi) => void;
  /**
   * Guarda o id da instrução devolvido pelo endpoint real quando a reconsulta ainda
   * não trouxe o item — o Desfazer precisa dele para chamar a API.
   */
  registrarInstrucao: (chave: string, itemId: string) => void;
  /** Limpa o estado persistido após descarte ou aplicação. */
  limparSimulacaoAtiva: () => void;
}

export const SimulacaoContext = createContext<SimulacaoContextValue | null>(null);

/** Estado do Modo Simulação. Só existe dentro do `SimulacaoProvider` da Carga de Trabalho. */
export function useSimulacao(): SimulacaoContextValue {
  const value = useContext(SimulacaoContext);
  if (!value) throw new Error("useSimulacao precisa estar dentro de um SimulacaoProvider.");
  return value;
}
